/**
 * API client for making HTTP requests with environment-specific configurations
 */

import { getEnvironmentConfig, envLog } from '../config/environment';

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: Record<string, string>;
  body?: any;
  timeout?: number;
}

export interface ApiResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public statusText: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * API client class for making environment-aware HTTP requests
 */
export class ApiClient {
  private environment: string;
  private baseUrl: string;
  private config: ReturnType<typeof getEnvironmentConfig>;

  constructor(environment: string = 'app') {
    this.environment = environment;
    this.config = getEnvironmentConfig(environment);
    this.baseUrl = this.config.baseUrl;
    
    envLog(environment, 'ApiClient initialized', { baseUrl: this.baseUrl });
  }

  /**
   * Make an HTTP request with automatic retries and timeout handling
   */
  async request<T = any>(
    endpoint: string,
    options: ApiRequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const {
      method = 'GET',
      headers = {},
      body,
      timeout = this.config.apiTimeout,
    } = options;

    const url = `${this.baseUrl}${endpoint}`;
    const requestHeaders = {
      'Content-Type': 'application/json',
      ...headers,
    };

    envLog(this.environment, `Making ${method} request to ${url}`);

    let lastError: Error | null = null;

    // Retry logic
    for (let attempt = 1; attempt <= this.config.retryAttempts; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        const response = await fetch(url, {
          method,
          headers: requestHeaders,
          body: body ? JSON.stringify(body) : undefined,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new ApiError(
            `HTTP ${response.status}: ${response.statusText}`,
            response.status,
            response.statusText
          );
        }

        const data = await response.json();
        
        envLog(this.environment, `Request successful on attempt ${attempt}`);
        
        return {
          data,
          status: response.status,
          statusText: response.statusText,
        };
      } catch (error) {
        lastError = error as Error;
        
        if (error instanceof ApiError || attempt === this.config.retryAttempts) {
          // Don't retry API errors or if we've reached max attempts
          break;
        }

        envLog(
          this.environment,
          `Request failed on attempt ${attempt}, retrying...`,
          error
        );

        // Wait before retrying (exponential backoff)
        if (attempt < this.config.retryAttempts) {
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        }
      }
    }

    // If we get here, all attempts failed
    throw lastError || new Error('Request failed after all retry attempts');
  }

  /**
   * Convenience method for GET requests
   */
  async get<T = any>(endpoint: string, headers?: Record<string, string>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET', headers });
  }

  /**
   * Convenience method for POST requests
   */
  async post<T = any>(
    endpoint: string,
    body?: any,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'POST', body, headers });
  }

  /**
   * Convenience method for PUT requests
   */
  async put<T = any>(
    endpoint: string,
    body?: any,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'PUT', body, headers });
  }

  /**
   * Convenience method for DELETE requests
   */
  async delete<T = any>(
    endpoint: string,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE', headers });
  }

  /**
   * Get the current base URL
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * Get the current environment
   */
  getEnvironment(): string {
    return this.environment;
  }
}

/**
 * Create an API client instance for a specific environment
 */
export const createApiClient = (environment: string = 'app'): ApiClient => {
  return new ApiClient(environment);
};

export default ApiClient;
